import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';
import type { OrgPayload } from '../../pages/admin/createOrg/types';

/** An organization row as returned by the backend. */
export interface Organization {
  id: number;
  name: string;
  short_name?: string | null;
  internal_code?: string | null;
  logo_url?: string | null;
  description?: string | null;
  status: string; // "Active" | "Inactive"
  address?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  /** Platform state this client belongs to (e.g. "RJ"). */
  state_code?: string | null;
  created_at?: string;
}

/** DRF PageNumberPagination envelope. */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Normalized list result the table consumes. */
export interface OrganizationsResponse {
  items: Organization[];
  total: number;
  page: number;
  pageSize: number;
}

/** Server echo of a created organization (fields beyond `id` are not relied upon). */
export interface CreatedOrganization extends Partial<OrgPayload> {
  id?: number | string;
}

/** One EC boundary row for the bulk scope call. */
export interface EcScopeArea {
  ec_location_level_id: number;
  area_code: string;
}
export interface EcScopeBulkRequest {
  organization: number;
  areas: EcScopeArea[];
  /** When true, the org's existing boundaries are cleared before inserting these. */
  replace?: boolean;
}
export interface EcScopeBulkResponse {
  created: unknown[];
  skipped_existing: number;
  total_now: number;
}

/** A saved EC boundary row for an org. */
export interface EcScope {
  id: number;
  organization: number;
  ec_location_level_id: number;
  area_code: string;
}

/** One organization level for the bulk save (client-side temp `id` + `parentId`). */
export interface LevelInput {
  id: string;
  name: string;
  parentId: string | null;
}
export interface LevelBulkRequest {
  organization: number;
  levels: LevelInput[];
  /** When true, the org's existing levels are cleared before inserting these. */
  replace?: boolean;
  /** "admin" scopes these to the administrative hierarchy (else the org hierarchy). */
  kind?: string;
}
export interface LevelBulkResponse {
  created: { temp_id: string; id: number; level_name: string; parent: number | null }[];
  id_map: Record<string, number>;
}

/** A saved organization level (GET) — `parent` is the parent level's id or null. */
export interface OrgLevel {
  id: number;
  level_name: string;
  parent: number | null;
  organization?: number;
  description?: string | null;
  status?: string;
}

/** A booth/area attached to an org node. `id` is the node-area record (for de-link / re-link). */
export interface OrgNodeArea {
  id?: number;
  area_code: string;
  booth_name?: string;
  booth_name_hi?: string;
  organisation_node?: number;
  /** The EC location level this area was mapped at — resolves the correct area-info `level`. */
  ec_location_level_id?: number | null;
}
/** One org node in the link picker (GET /organisation/node-area-map/). */
export interface NodeAreaMapNode {
  node_id: number;
  node_name: string;
  level_name?: string | null;
  /** The EC level this node maps to; null = not linkable. */
  ec_level?: string | null;
}
export interface NodeAreaMap {
  organization: number;
  kind: string;
  count: number;
  nodes: NodeAreaMapNode[];
}

/** A node in the org's internal hierarchy tree (GET /organisation/node/tree/). */
export interface OrgNode {
  /** organisation_node id — the re-link (move) target. */
  id?: number;
  org_name: string;
  level_name: string;
  children: OrgNode[];
  areas: OrgNodeArea[];
}

/** A named/coded node inside a boundary's context chain. */
export interface BoundaryContextNode {
  name?: string | null;
  code?: string | null;
}
/** The org's selected EC boundary + its ancestry (GET /organisation/boundary/). */
export interface BoundaryDto {
  id?: number;
  ec_location_level_id?: number | null;
  level_name?: string | null;
  area_code?: string | null;
  area_name?: string | null;
  context?: {
    state?: BoundaryContextNode | null;
    district?: BoundaryContextNode | null;
    parliament?: BoundaryContextNode | null;
    assembly?: BoundaryContextNode | null;
    rural_urban?: string | null;
    gram_panchayat?: BoundaryContextNode | null;
    village?: BoundaryContextNode | null;
  } | null;
}
/** The boundary endpoint may return one object, an array, or a wrapped envelope. */
const asBoundaryList = (r: unknown): BoundaryDto[] => {
  if (!r) return [];
  if (Array.isArray(r)) return r as BoundaryDto[];
  const obj = r as Record<string, unknown>;
  // Array envelopes: { selected | results | boundaries | data: [...] }.
  for (const key of ['selected', 'results', 'boundaries', 'data'] as const) {
    if (Array.isArray(obj[key])) return obj[key] as BoundaryDto[];
  }
  // A single boundary object (has any of the expected fields).
  if (obj.area_code || obj.level_name || obj.context) return [r as BoundaryDto];
  // A single boundary wrapped under a key.
  for (const key of ['boundary', 'data'] as const) {
    if (obj[key] && typeof obj[key] === 'object') return [obj[key] as BoundaryDto];
  }
  return [];
};

/** Organizations endpoints, injected into the shared `baseApi`. */
export const organizationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOrganizations: build.query<
      OrganizationsResponse,
      { page?: number; pageSize?: number; all?: boolean; stateCode?: string } | void
    >({
      query: (arg) => ({
        url: `${ApiEndPoints.organization}/`,
        // `all` fetches the full list (no pagination) — used for dropdowns.
        params: {
          ...(arg?.all ? {} : { page: arg?.page ?? 1, page_size: arg?.pageSize ?? 10 }),
          ...(arg?.stateCode ? { state_code: arg.stateCode } : {}),
        },
      }),
      transformResponse: (r: Paginated<Organization> | Organization[], _meta, arg) => {
        const items = Array.isArray(r) ? r : (r?.results ?? []);
        const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
        return { items, total, page: arg?.page ?? 1, pageSize: arg?.pageSize ?? 10 };
      },
      providesTags: ['Organizations'],
    }),
    getOrganization: build.query<Organization, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.organization}/${id}/` }),
      providesTags: ['Organizations'],
    }),
    createOrganization: build.mutation<CreatedOrganization, OrgPayload>({
      query: (body) => ({
        url: `${ApiEndPoints.organization}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organizations'],
    }),
    updateOrganization: build.mutation<
      Organization,
      { id: number | string; patch: Partial<OrgPayload> }
    >({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.organization}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Organizations'],
    }),
    deleteOrganization: build.mutation<void, number | string>({
      query: (id) => ({
        url: `${ApiEndPoints.organization}/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organizations'],
    }),
    // Step 2 — the org's saved boundaries (edit/view prefill).
    getEcScopes: build.query<EcScope[], { organization: number | string }>({
      query: ({ organization }) => ({
        url: `${ApiEndPoints.organizationEcScope}/`,
        params: { organization, page_size: 1000 },
      }),
      transformResponse: (r: Paginated<EcScope> | EcScope[]) =>
        Array.isArray(r) ? r : (r?.results ?? []),
      providesTags: ['EcScope'],
    }),
    // Step 2 — save the org's selected EC boundaries in one call.
    createEcScopeBulk: build.mutation<EcScopeBulkResponse, EcScopeBulkRequest>({
      query: (body) => ({
        url: `${ApiEndPoints.organizationEcScopeBulk}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EcScope'],
    }),
    // Step 3 / 5 — the org's saved level hierarchy (edit/view prefill). kind="admin"
    // returns the administrative levels (step 5) instead of the org levels (step 3).
    getLevels: build.query<OrgLevel[], { organization: number | string; kind?: string }>({
      query: ({ organization, kind }) => ({
        url: `${ApiEndPoints.organizationLevel}/`,
        params: { organization, ...(kind ? { kind } : {}) },
      }),
      transformResponse: (r: Paginated<OrgLevel> | OrgLevel[]) =>
        Array.isArray(r) ? r : (r?.results ?? []),
      providesTags: ['OrgLevel'],
    }),
    // Step 3 — save the organization level hierarchy in one call.
    createLevelsBulk: build.mutation<LevelBulkResponse, LevelBulkRequest>({
      query: (body) => ({
        url: `${ApiEndPoints.organizationLevelBulk}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['OrgLevel'],
    }),
    // Step 4 — download the per-org hierarchy Excel template (returns a blob).
    getHierarchyTemplate: build.query<Blob, { organization: number | string; kind?: string }>({
      query: ({ organization, kind }) => ({
        url: `${ApiEndPoints.organizationHierarchyTemplate}/`,
        params: { organization, ...(kind ? { kind } : {}) },
        // xlsx on success → blob; JSON error body (e.g. "no levels yet") → parsed object.
        responseHandler: (response) => {
          const ct = response.headers.get('content-type') || '';
          return ct.includes('application/json') ? response.json() : response.blob();
        },
        cache: 'no-cache',
      }),
    }),
    // Step 4 — download the org's already-uploaded hierarchy Excel (returns a blob).
    getHierarchyExport: build.query<Blob, { organization: number | string; kind?: string }>({
      query: ({ organization, kind }) => ({
        url: `${ApiEndPoints.organizationHierarchyExport}/`,
        params: { organization, ...(kind ? { kind } : {}) },
        responseHandler: (response) => {
          const ct = response.headers.get('content-type') || '';
          return ct.includes('application/json') ? response.json() : response.blob();
        },
        cache: 'no-cache',
      }),
    }),
    // Step 4 — upload the filled hierarchy Excel (multipart).
    uploadHierarchy: build.mutation<
      unknown,
      { organization: number | string; file: File; kind?: string }
    >({
      query: ({ organization, file, kind }) => {
        const form = new FormData();
        form.append('organization', String(organization));
        form.append('file', file);
        if (kind) form.append('kind', kind);
        return {
          url: `${ApiEndPoints.organizationHierarchyUpload}/`,
          method: 'POST',
          body: form,
        };
      },
      // Refresh the internal-org tree after a successful import.
      invalidatesTags: ['OrgNode'],
    }),
    // Step 4 — the org's internal hierarchy tree (left panel, populated by upload).
    getNodeTree: build.query<
      OrgNode[],
      { organization: number | string; kind?: string; search?: string }
    >({
      query: ({ organization, kind, search }) => ({
        url: `${ApiEndPoints.organizationNodeTree}/`,
        params: { organization, ...(kind ? { kind } : {}), ...(search ? { search } : {}) },
      }),
      transformResponse: (r: OrgNode[] | { results?: OrgNode[] }) =>
        Array.isArray(r) ? r : (r?.results ?? []),
      providesTags: ['OrgNode'],
    }),
    // Flat list of org nodes with the EC level each maps to — drives the link picker
    // (only nodes with an `ec_level` matching the unit being linked are selectable).
    getNodeAreaMap: build.query<
      NodeAreaMapNode[],
      { organization: number | string; kind?: string; mapped?: boolean }
    >({
      query: ({ organization, kind, mapped }) => ({
        url: `${ApiEndPoints.organizationNodeAreaMap}/`,
        params: {
          organization,
          ...(kind ? { kind } : {}),
          ...(mapped !== undefined ? { mapped } : {}),
        },
      }),
      transformResponse: (r: NodeAreaMap | NodeAreaMapNode[]) =>
        Array.isArray(r) ? r : (r?.nodes ?? []),
      providesTags: ['OrgNode'],
    }),
    // The signed-in user's own org subtree — the response wraps it as
    // `{ organization, tree: [...] }`; we keep both.
    getMyTree: build.query<{ organization: number | null; tree: OrgNode[] }, void>({
      query: () => ({ url: `${ApiEndPoints.organizationNodeMyTree}/` }),
      transformResponse: (
        r: OrgNode[] | { organization?: number | null; tree?: OrgNode[]; results?: OrgNode[] },
      ) =>
        Array.isArray(r)
          ? { organization: null, tree: r }
          : { organization: r?.organization ?? null, tree: r?.tree ?? r?.results ?? [] },
      providesTags: ['OrgNode'],
    }),
    // The org's selected EC boundaries + ancestry (scopes the engagement EC-Level tree).
    getBoundaries: build.query<BoundaryDto[], { organization: number | string }>({
      query: ({ organization }) => ({
        url: `${ApiEndPoints.organizationBoundary}/`,
        params: { organization },
      }),
      transformResponse: asBoundaryList,
      providesTags: ['EcScope'],
    }),
    // Step 4 — link a booth to an org node (create a node-area mapping).
    createNodeArea: build.mutation<
      unknown,
      {
        organisation_node: number;
        area_code: string;
        booth_name?: string;
        ec_location_level_id?: number | null;
      }
    >({
      query: (body) => ({
        url: `${ApiEndPoints.organizationNodeArea}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['OrgNode'],
    }),
    // Step 4 — de-link a booth from its org node (remove the node-area mapping).
    deleteNodeArea: build.mutation<void, number>({
      query: (id) => ({
        url: `${ApiEndPoints.organizationNodeArea}/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['OrgNode'],
    }),
    // Step 4 — re-link a booth: move the node-area to another org node (SK).
    updateNodeArea: build.mutation<unknown, { id: number; organisation_node: number }>({
      query: ({ id, organisation_node }) => ({
        url: `${ApiEndPoints.organizationNodeArea}/${id}/`,
        method: 'PATCH',
        body: { organisation_node },
      }),
      invalidatesTags: ['OrgNode'],
    }),
  }),
});

export const {
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetEcScopesQuery,
  useCreateEcScopeBulkMutation,
  useGetLevelsQuery,
  useCreateLevelsBulkMutation,
  useLazyGetHierarchyTemplateQuery,
  useLazyGetHierarchyExportQuery,
  useUploadHierarchyMutation,
  useGetNodeTreeQuery,
  useGetNodeAreaMapQuery,
  useGetMyTreeQuery,
  useGetBoundariesQuery,
  useCreateNodeAreaMutation,
  useDeleteNodeAreaMutation,
  useUpdateNodeAreaMutation,
} = organizationsApi;
